import React, { useId, useRef } from "react";
import styled from "styled-components";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "store";
import { useAppDispatch } from "store/hooks";
import { logout } from "store/Auth/thunks";
import { CloseIcon, DeleteIcon, ExternalIcon } from "components/Icons";
import { Button } from "components/Button";
import { ASIAccountSwitcher } from "components/ASIAccountSwitcher";
import { DeleteWalletModal } from "components/DeleteWalletModal";
import { VisuallyHidden } from "components/Foundation";
import { NetworkSelector } from "components/NetworkSelector";
import {
    useBodyScrollLock,
    useDeleteActiveWallet,
    useFocusTrap,
} from "hooks";
import { selectIsNetworkOperationPending } from "store/networkOperationSlice";

const MobileNavDrawerStyled = styled.div<{ $isOpen: boolean }>`
    position: fixed;
    top: 0;
    right: ${({ $isOpen }) => ($isOpen ? "0" : "-100%")};
    width: 80%;
    max-width: 320px;
    height: 100vh;
    visibility: ${({ $isOpen }) => ($isOpen ? "visible" : "hidden")};
    background: ${({ theme }) => theme.card};
    border-left: 1px solid ${({ theme }) => theme.border};
    transition: right ${({ theme }) => theme.motion.slow}
        ${({ theme }) => theme.motion.easing};
    z-index: ${({ theme }) => theme.zIndices.drawer};
    overflow-y: auto;
    outline: none;

    display: flex;
    flex-direction: column;

    @media (min-width: calc(${({ theme }) => theme.breakpoints.navigation} + 1px)) {
        display: none;
    }
`;

const MobileNavOverlayStyled = styled.div<{ $isOpen: boolean }>`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${({ theme }) => theme.overlay};
    display: ${({ $isOpen }) => ($isOpen ? "block" : "none")};
    z-index: ${({ theme }) => theme.zIndices.drawer - 1};

    @media (min-width: calc(${({ theme }) => theme.breakpoints.navigation} + 1px)) {
        display: none;
    }
`;

const MobileNavHeader = styled.div`
    padding: ${({ theme }) => theme.spacing.xl};
    border-bottom: 1px solid ${({ theme }) => theme.border};
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const MobileNavContent = styled.div`
    padding: ${({ theme }) => theme.spacing.xl};
`;

const MobileNetworkSection = styled.div`
    width: 100%;
    margin-bottom: 36px;
`;

const MobileNavSection = styled.div``;

const MobileNavFooter = styled.div`
    padding: ${({ theme }) => theme.spacing["3xl"]};
    margin-top: auto;
`;

const MobileNavLink = styled.button<{ $active: boolean }>`
    width: 100%;
    padding: ${({ theme }) => theme.spacing.lg}
        ${({ theme }) => theme.spacing.xl};
    background: ${({ $active, theme }) =>
        $active ? theme.primary + "20" : "transparent"};
    border: none;
    border-left: 3px solid
        ${({ $active, theme }) => ($active ? theme.primary : "transparent")};
    color: ${({ $active, theme }) =>
        $active ? theme.primary : theme.text.secondary};
    font-weight: ${({ $active }) => ($active ? "600" : "400")};
    cursor: pointer;
    transition:
        color ${({ theme }) => theme.motion.normal}
            ${({ theme }) => theme.motion.easing},
        background ${({ theme }) => theme.motion.normal}
            ${({ theme }) => theme.motion.easing};
    text-align: left;
    font-size: ${({ theme }) => theme.typography.size.sm};
    margin-bottom: ${({ theme }) => theme.spacing.xs};
    border-radius: ${({ theme }) => theme.radii.xs};

    &:hover {
        background: ${({ theme }) => theme.surface};
        color: ${({ theme }) => theme.primary};
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.4;
    }

    &:focus-visible {
        outline: none;
        box-shadow: inset 0 0 0 2px ${({ theme }) => theme.focusRing};
    }
`;

const ExternalNavLinkMobile = styled(MobileNavLink)`
    color: ${({ theme }) => theme.text.primary};
    display: flex;
    align-items: center;
    gap: 5px;
`;

const DelimiterLine = styled.div`
    width: 100%;
    height: 1px;
    background: ${({ theme }) => theme.border};
    margin: ${({ theme }) => theme.spacing.xl} auto;
`;

const DangerButton = styled(Button)`
    border-color: ${({ theme }) => theme.danger};
    color: ${({ theme }) => theme.danger};

    &:hover:not(:disabled) {
        background: ${({ theme }) => theme.danger}1F;
    }
`;

const LogoutButton = styled(DangerButton)`
    margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const IconButton = styled.button`
    padding: ${({ theme }) => theme.spacing.md};
    border: 1px solid ${({ theme }) => theme.border};
    border-radius: ${({ theme }) => theme.radii.sm};
    background: ${({ theme }) => theme.surface};
    color: ${({ theme }) => theme.text.primary};
    cursor: pointer;
    transition:
        background ${({ theme }) => theme.motion.normal}
            ${({ theme }) => theme.motion.easing},
        color ${({ theme }) => theme.motion.normal}
            ${({ theme }) => theme.motion.easing};
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;

    &:hover {
        background: ${({ theme }) => theme.primary};
        color: ${({ theme }) => theme.text.inverse};
    }

    &:focus-visible {
        outline: none;
        box-shadow: 0 0 0 2px ${({ theme }) => theme.focusRing};
    }
`;

const externalLinksSet = [
    { path: `${process.env.REACT_APP_EXPLORER_URL}`, label: "Explorer" },
    { path: `${process.env.REACT_APP_FAUCET_URL}`, label: "Faucet" },
];

const openExternalLink = (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
};

interface NavItem {
    path: string;
    label: string;
}

interface MobileNavDrawerComponentProps {
    isOpen: boolean;
    navItems: NavItem[];
    onClose: () => void;
}

export const MobileNavDrawerComponent: React.FC<
    MobileNavDrawerComponentProps
> = ({ isOpen, navItems, onClose }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();
    const isAuthenticated = useSelector(
        (state: RootState) => state.auth.isAuthenticated,
    );
    const isNetworkOperationPending = useSelector(
        selectIsNetworkOperationPending,
    );
    const drawerRef = useRef<HTMLDivElement>(null);
    const closeButtonRef = useRef<HTMLButtonElement>(null);
    const titleId = useId().replace(/:/g, "");

    const deleteWallet = useDeleteActiveWallet();

    useBodyScrollLock(isOpen);
    useFocusTrap(drawerRef, {
        active: isOpen,
        initialFocusRef: closeButtonRef,
        onEscape: onClose,
        isPaused: () => deleteWallet.isOpen,
        restoreFocus: true,
    });

    const handleNavigation = (path: string) => {
        if (isNetworkOperationPending) return;

        navigate(path);
        onClose();
    };

    const handleExternalNavigation = (url: string) => {
        openExternalLink(url);
        onClose();
    };

    const handleLogout = () => {
        if (isNetworkOperationPending) return;

        dispatch(logout());
        navigate("/login");
        onClose();
    };

    return (
        <>
            <MobileNavOverlayStyled
                $isOpen={isOpen}
                onClick={onClose}
                aria-hidden="true"
            />
            <MobileNavDrawerStyled
                id="mobile-nav-drawer"
                className="mobile-nav-drawer-styled"
                ref={drawerRef}
                $isOpen={isOpen}
                role="dialog"
                aria-modal="true"
                aria-labelledby={`mobile-nav-title-${titleId}`}
                aria-hidden={!isOpen}
                tabIndex={-1}
                data-modal-overlay
            >
                <MobileNavHeader>
                    <h2
                        id={`mobile-nav-title-${titleId}`}
                        style={{ margin: 0, fontSize: "18px" }}
                    >
                        Menu
                    </h2>
                    <IconButton
                        ref={closeButtonRef}
                        type="button"
                        aria-label="Close navigation"
                        onClick={onClose}
                    >
                        <CloseIcon size={20} />
                    </IconButton>
                </MobileNavHeader>

                <MobileNavContent>
                    <MobileNavSection>
                        {navItems.map((item) => (
                            <MobileNavLink
                                key={item.path}
                                type="button"
                                $active={location.pathname === item.path}
                                aria-current={
                                    location.pathname === item.path
                                        ? "page"
                                        : undefined
                                }
                                onClick={() => handleNavigation(item.path)}
                                disabled={isNetworkOperationPending}
                            >
                                {item.label}
                            </MobileNavLink>
                        ))}
                        <DelimiterLine />
                        {externalLinksSet.map((item) => (
                            <ExternalNavLinkMobile
                                $active={false}
                                key={item.path}
                                type="button"
                                className="text-1"
                                aria-label={`${item.label} (opens in a new tab)`}
                                onClick={() =>
                                    handleExternalNavigation(item.path)
                                }
                            >
                                {item.label}
                                <ExternalIcon />
                            </ExternalNavLinkMobile>
                        ))}
                    </MobileNavSection>
                </MobileNavContent>
                <MobileNavFooter>
                    <ASIAccountSwitcher
                        adaptive={false}
                        layout="vertical"
                        fullWidth
                        listDirection="top"
                        wrapperStyle={{ marginBottom: "16px" }}
                    />
                    <MobileNetworkSection>
                        <VisuallyHidden id="mobile-network-selector-label">
                            Network
                        </VisuallyHidden>
                        <NetworkSelector
                            id="mobile-network-selector"
                            aria-labelledby="mobile-network-selector-label"
                            style={{ width: "100%", minWidth: 0 }}
                            variant="default"
                            listDirection="top"
                        />
                    </MobileNetworkSection>
                    {isAuthenticated && (
                        <LogoutButton
                            fullWidth
                            variant="secondary"
                            onClick={handleLogout}
                            disabled={isNetworkOperationPending}
                        >
                            Logout
                            <svg
                                width="20"
                                height="18"
                                viewBox="0 0 20 18"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                aria-hidden="true"
                            >
                                <path
                                    d="M2 2L10 2L10 0L2 6.99382e-07C0.899998 7.95547e-07 -1.49493e-06 0.9 -1.39876e-06 2L-1.74846e-07 16C-7.86805e-08 17.1 0.9 18 2 18L10 18L10 16L2 16L2 2Z"
                                    fill="currentcolor"
                                />
                                <path
                                    d="M15 4L13.6 5.4L16.2 8H6V10H16.2L13.6 12.6L15 14L20 9L15 4Z"
                                    fill="currentcolor"
                                />
                            </svg>
                        </LogoutButton>
                    )}
                    {isAuthenticated && (
                        <DangerButton
                            fullWidth
                            variant="secondary"
                            onClick={deleteWallet.open}
                            disabled={isNetworkOperationPending}
                        >
                            Delete Wallet
                            <DeleteIcon size={20} />
                        </DangerButton>
                    )}
                </MobileNavFooter>
            </MobileNavDrawerStyled>
            <DeleteWalletModal
                isOpen={deleteWallet.isOpen}
                isDeleting={deleteWallet.isDeleting}
                error={deleteWallet.error}
                onConfirm={deleteWallet.confirm}
                onCancel={deleteWallet.close}
            />
        </>
    );
};
